import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Layout, Radio, Button, Progress } from "antd";
import moment from "moment";
import _ from "lodash";

import {
  ButtonChartTooltips,
  ButtonChartTooltipsTypes,
  ButtonViews,
  ButtonViewTypes,
  IconLight,
  Spinner
} from "../components";
import CoreDataTransformations from "../core/_utils/coreDataTransformations";
import {
  MENU_REPORT_ACTIONS,
  REPORT_ACTIONS,
  REPORT_STATUS,
  SELECTOR_GROUP_TYPE,
  SELECTOR_TYPE,
  REQUEST_STATUS
} from "../core/_utils/constants";
import { DataTransformations } from "../../utils/data_transformations";
import { ErrorIcon } from "../components/generic";
import { CoreReportMenu } from "../core/core_report_menu";
import { CoreReportProperties } from "../core/core_report_properties";
import { CoreReport } from "../core/core_report";
import { PerformanceToolbar } from "./performance_toolbar";
import "./performance.css";

class Performance extends Component {
  state = {
    settings: {
      view: ButtonViewTypes.Table, //default, otherwise will be overwritten by settings prop
      selectedChartTooltipsType: ButtonChartTooltipsTypes.Multiple,
      selectedGroupTypes: [SELECTOR_GROUP_TYPE.UNIT],
      isCatalogueCollapsed: false,
      isReportPropsCollapsed: false,
      startDt: moment()
        .add(-7, "days")
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss"),
      endDt: "",
      selectedTimeAggregation: "day",
      selectedTimePeriod: "",
      numberOfChartsToShow: 2,
      showLegend: true,
      ...this.props.settings //test with reports array is correct?
    },
    processed: {},
    tableWidth: 1000,
    tableHeight: 500,
    siderHeight: 500,
    selectedCollectionReportIndex: 0,
    isFullScreen: false,
    requests: {},
    lastRequestTime: null,
    fetchData: {},
    options: {
      groupTypes: []
    },
    themes: {},
    reports: {},
    collection: [],

    page: {
      loading: true,
      error: false
    },
    walkthrough: {
      show: false, //temp
      version: 0,
      steps: []
    }
  };

  static fetchInitData() {
    return window.axiosClient.get("/performance_init_data");
  }

  static fetchUnitObjects() {
    return window.axiosClient.get("/performance_objects?objectType=unit"); //todo should fetch based on group types return by init data, but this should do for now
  }

  componentDidMount() {
    this.props.onLogAction("", "load", { state: this.state });
    const idToken = localStorage.getItem("id_token");
    //add id token before requests tp header
    window.axiosClient.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${idToken}`;

    //initial load
    const self = this;

    axios
      .all([Performance.fetchInitData(), Performance.fetchUnitObjects()])
      .then(
        axios.spread(function(initDataResponse, unitObjectsResponse) {
          const unitObjectsProcessed = CoreDataTransformations.getOptionsBySelectorType(
            SELECTOR_TYPE.UNIT_OBJECTS,
            unitObjectsResponse.data
          );

          self.setState(prevState => ({
            ...prevState,
            tableWidth: DataTransformations.getTableWidth(),
            tableHeight: DataTransformations.getTableHeight(),
            siderHeight: DataTransformations.getSiderHeight(),
            page: {
              ...prevState.page,
              loading: false
            },
            options: {
              ...prevState.options,

              [SELECTOR_TYPE.UNIT_VARIABLES]:
                initDataResponse.data.unit_variables,
              [SELECTOR_TYPE.UNIT_OBJECTS]: unitObjectsProcessed,
              timeAggregation: initDataResponse.data.time_agg_list,
              groupTypes: initDataResponse.data.element_group_types
            },
            themes: initDataResponse.data.themes,
            reports: initDataResponse.data.reports
          }));
        })
      )
      .catch(error => {
        console.error("Error occurred loading page", error);
        self.setState(prevState => ({
          ...prevState,
          page: {
            ...prevState.page,
            loading: false,
            error: true,
            "error-msg": error.message
          }
        }));
      });

    //on resize, reset table width and height
    window.onresize = () => {
      this.setState({
        tableHeight: DataTransformations.getTableHeight(),
        tableWidth: DataTransformations.getTableWidth(),
        siderHeight: DataTransformations.getSiderHeight()
      });
    };
  }

  logClickAction = (component, payload) => {
    this.props.onLogAction(component, "click", payload);
  };

  logFetchAction = (component, payload) => {
    this.props.onLogAction(component, "fetch", payload);
  };

  logError = (component, payload) => {
    this.props.onLogAction(component, "error", payload);
  };

  onChangeView = view => {
    this.logClickAction("view", { target: view });
    this.setState(
      prevState => ({
        settings: {
          ...prevState.settings,
          view: view
        }
      }),
      () => this.props.onUpdateSettings(this.state.settings)
    );
  };

  onChangeChartTooltipsType = chartTooltipsType => {
    this.logClickAction("chartTooltipsType", { target: chartTooltipsType });
    this.setState(
      prevState => ({
        settings: {
          ...prevState.settings,
          selectedChartTooltipsType: chartTooltipsType
        }
      }),
      () => this.props.onUpdateSettings(this.state.settings)
    );
  };

  onCollapseCatalogue = (collapsed, type) => {
    this.setState(
      prevState => ({
        settings: {
          ...prevState.settings,
          isCatalogueCollapsed: collapsed
        }
      }),
      () => {
        this.props.onUpdateSettings(this.state.settings);
      }
    );
  };

  onUpdateCollectionReportProperties = collectionReportIndex => newValues => {
    //  console.log("Update props", collectionReportIndex, newValues);
    let newCollectionState = Object.assign(this.state.collection);

    newCollectionState[collectionReportIndex] = newValues;

    this.setState(prevState => ({
      collection: newCollectionState
    }));
  };

  onTimePeriodUpdate = (startDt, endDt, timeAggregation, timePeriod, doRun) => {
    //const dtRange = `${startDt}-${endDt}`;
    this.setState(
      prevState => ({
        ...prevState,
        settings: {
          ...prevState.settings,
          startDt: startDt,
          endDt: endDt,
          selectedTimeAggregation: timeAggregation,
          selectedTimePeriod: timePeriod
        }
        // report: {
        //   ...prevState.report,
        //   data: {
        //     ...prevState.report.data,
        //     [dtRange]: null
        //   }
        // }
      }),
      () => {
        this.props.onUpdateSettings(this.state.settings);
        if (doRun) this.onRunAllReports();
      }
    );
  };

  onRunAllReports = () => {
    const { collection } = this.state;
    this.logClickAction("runAllReport", {
      totalReports: collection && collection.length
    });
    const requestTime = this.getCurrentTime();
    collection.forEach((item, i) => {
      this.onRunCollectionReport(i, requestTime);
    });
  };

  getCurrentTime = () => {
    const now = new Date().getTime();
    this.setState({
      lastRequestTime: now
    });
    return now;
  };

  onRunCollectionReport = (collectionReportIndex, requestTime) => {
    const { settings, options } = this.state;
    const { selectedTimeAggregation, startDt, endDt } = settings;

    if (!requestTime) requestTime = this.getCurrentTime();

    // this.updateCollectionReportStatus(
    //   collectionReportIndex,
    //   REPORT_STATUS.LOADING
    // );

    const selectedReport = this.getCollectionReportByIndex(
      collectionReportIndex
    );

    const groupTypes = CoreDataTransformations.getGroupTypesFromReport(
      selectedReport
    );

    groupTypes.forEach(groupType => {
      const selectedElements =
        selectedReport.groups[groupType].elements.selectedValues;
      const selectedVariables =
        selectedReport.groups[groupType].variables.selectedValues;
      if (
        selectedElements &&
        selectedVariables &&
        selectedElements.length > 0 &&
        selectedVariables.length > 0
      ) {
        const variableOptions = options[`${groupType}_variables`];

        const elementOptions = options[`${groupType}_objects`];

        const checkedElements = CoreDataTransformations.getEnabledSelectedValues(
          elementOptions,
          selectedElements,
          null //TOFIX no data mode in this page
        );
        const checkedVariables = CoreDataTransformations.getEnabledSelectedOptions(
          variableOptions,
          selectedVariables,
          null //TOFIX no data mode in this page
        );

        const fetchKey = this.getFetchKey(
          startDt,
          endDt,
          selectedTimeAggregation
        );
        // console.log(
        //   "calling fetchdata",
        //   fetchKey,
        //   selectedReport,
        //   checkedVariables,
        //   checkedElements
        // );
        this.updateCollectionReportFetchKey(collectionReportIndex, fetchKey);
        this.updateCollectionReportRequestTime(
          collectionReportIndex,
          requestTime
        );

        this.fetchData(
          fetchKey,
          startDt,
          endDt,
          selectedTimeAggregation,
          groupType,
          checkedVariables,
          checkedElements,
          requestTime
        );
      }
    });
  };

  updateCollectionReportRequestTime = (collectionReportIndex, requestTime) => {
    let reportProps = this.getCollectionReportByIndex(collectionReportIndex);
    reportProps.requestTime = requestTime;

    this.onUpdateCollectionReportProperties(collectionReportIndex)(reportProps);
  };

  updateCollectionReportFetchKey = (collectionReportIndex, fetchKey) => {
    let reportProps = this.getCollectionReportByIndex(collectionReportIndex);
    reportProps.fetchKey = fetchKey;

    this.onUpdateCollectionReportProperties(collectionReportIndex)(reportProps);
  };

  fetchData(
    fetchKey,
    startDt,
    endDt,
    groupByPeriod,
    groupType,
    variables,
    objects,
    requestTime
  ) {
    if (
      !groupType ||
      !variables ||
      variables.length < 1 ||
      !objects ||
      objects.length < 1
    )
      return; // do nothing as not all elements are selected

    this.logFetchAction("fetchData", {
      fetchKey,
      startDt,
      endDt,
      groupByPeriod,
      groupType,
      variables,
      objects
    });

    const self = this;
    //reset - should be done by parent before calling this function
    // self.setState(prevState => ({
    //   [objectType]: {
    //     ...prevState[objectType],
    //     data: null
    //   }
    // }));

    let endpoints = {};
    //add system variables
    variables.forEach(v => {
      const fetchUrl = `${v.endpoint}?objectType=${groupType}`;
      if (!endpoints[fetchUrl])
        endpoints[fetchUrl] = {
          variables: []
        };
      endpoints[fetchUrl].variables.push(v.value);
    });

    const requests = self.state.requests;
    objects.forEach((obj, i) => {
      Object.keys(endpoints).forEach((ekey, j) => {
        //construct url
        const reportUrl = `/${ekey}&groupByPeriod=${groupByPeriod}&settStartDate=${encodeURIComponent(
          startDt
        )}&settEndDate=${encodeURIComponent(
          endDt
        )}&variableList=${encodeURIComponent(
          endpoints[ekey].variables.join(",")
        )}&elementNo=${obj}`;

        //console.log("Fetching reportUrl:", reportUrl);

        self.setState(prevState => ({
          requests: {
            ...prevState.requests,
            [reportUrl]: {
              ...prevState.requests[reportUrl],
              status: REQUEST_STATUS.LOADING,
              fetchKey: fetchKey,
              startDt: startDt,
              endDt: endDt,
              groupByPeriod: groupByPeriod,
              groupType: groupType,
              element: obj,
              variables: endpoints[ekey].variables.join(","),
              requestTime: requestTime
            }
          }
        }));

        //check if already fetched, then don't re-fetch
        if (
          requests &&
          requests[reportUrl] &&
          requests[reportUrl].status &&
          requests[reportUrl].status !== REQUEST_STATUS.ERROR
        ) {
          //still does not capture all same requests, but good enough for now.
          // console.log(
          //   "Not fetching as already fetch(ing)(ed), reportUrl:",
          //   reportUrl
          // );
          self.setState(prevState => ({
            requests: {
              ...prevState.requests,
              [reportUrl]: {
                ...prevState.requests[reportUrl],
                status: REQUEST_STATUS.SUCCESS
              }
            }
          }));
          return;
        }

        //fetch from server
        window.axiosClient
          .get(reportUrl)
          .then(response => {
            // console.log(`Received response for ${reportUrl}`, response);
            let data = {};
            if (
              self.state.fetchData &&
              self.state.fetchData[fetchKey] &&
              self.state.fetchData[fetchKey][groupType]
            )
              data = Object.assign(
                {},
                self.state.fetchData[fetchKey][groupType] //if already exists, copy from other request response
              );
            if (!data[obj]) data[obj] = {};
            if (response && response.data) {
              //transform data
              response.data.forEach(d => {
                let dt = d["settlementdate"]; //5min data
                if (!dt) dt = d["time4sec"]; //4sec data

                if (!data[obj][dt]) data[obj][dt] = {};

                Object.keys(d).forEach(dk => {
                  if (dk !== "settlementdate" && dk !== "time4sec")
                    data[obj][dt][dk] = d[dk];
                });
              });
            }

            self.setState(prevState => ({
              fetchData: {
                ...prevState.fetchData,
                [fetchKey]: {
                  ...prevState.fetchData[fetchKey],
                  [groupType]: data
                }
              },
              requests: {
                ...prevState.requests,
                [reportUrl]: {
                  ...prevState.requests[reportUrl], //store data here if want to do caching.
                  status: REQUEST_STATUS.SUCCESS
                }
              }
            }));
          })
          .catch(error => {
            const errorMsg = `Error fetching ${groupType}_${i}_${j}(${reportUrl})`;
            console.error(errorMsg, error);
            this.logError("fetchData", { errorMsg, error });

            self.setState(prevState => ({
              // fetchData: {
              //   ...prevState.fetchData,
              //   [fetchKey]: {
              //     ...prevState.fetchData[fetchKey],
              //     [groupType]: null//???
              //   }
              // },
              requests: {
                ...prevState.requests,
                [reportUrl]: {
                  ...prevState.requests[reportUrl],
                  status: REQUEST_STATUS.ERROR
                }
              }
            }));
          });
      });
    });
  }

  onMenuReportAction = (reportId, action) => {
    const { reports, selectedCollectionReportIndex } = this.state;
    switch (action) {
      case MENU_REPORT_ACTIONS.ADD:
        this.onAddCollectionReport(selectedCollectionReportIndex, {
          reportId: reportId,
          ..._.cloneDeep(reports[reportId])
        });
        break;
      case MENU_REPORT_ACTIONS.DELETE:
        this.onDeleteMenuReport(reportId);
        break;
      default:
        console.error("NOT IMPLEMENTED - menu report action:", action);
        break;
    }
    this.logClickAction("menuReport", { reportId: reportId, action: action });
  };

  onDeleteMenuReport = reportId => {
    let newReportsState = Object.assign(this.state.reports);
    delete newReportsState[reportId];
    this.setState({ reports: newReportsState });
  };

  onUpdateSelectedCollectionReportIndex = collectionReportIndex => {
    this.setState(
      prevState => ({
        selectedCollectionReportIndex: collectionReportIndex
      })
      //() => this.props.onUpdateSettings(this.state.settings)
    );
  };

  getSimilarReportCounter = item => {
    const { collection } = this.state;

    const similarReports = collection.filter(i => i.reportId === item.reportId);
    return similarReports ? similarReports.length : 0;
  };

  onAddCollectionReport = (collectionReportIndex, item) => {
    let newCollectionState = Object.assign(this.state.collection);
    newCollectionState.splice(collectionReportIndex, 0, {
      ...item,
      similarReportCounter: this.getSimilarReportCounter(item)
    });

    this.setState({ collection: newCollectionState });
  };

  onDeleteCollectionReport = collectionReportIndex => {
    let newCollectionState = Object.assign(this.state.collection);
    newCollectionState.splice(collectionReportIndex, 1);

    this.setState({ collection: newCollectionState });
  };

  onSwapCollectionReport = (index1, index2) => {
    //swap two items based on indexes, used for moving items up or down
    let newCollectionState = Object.assign(this.state.collection);
    const itemCount = newCollectionState.length;
    if (
      index1 < itemCount &&
      index2 < itemCount &&
      index1 >= 0 &&
      index2 >= 0
    ) {
      //check indexes are in bound
      const temp1 = newCollectionState[index1];
      newCollectionState[index1] = newCollectionState[index2];
      newCollectionState[index2] = temp1;
      this.setState({ collection: newCollectionState });

      return true;
    }
    return false;
  };

  onCollectionReportAction = collectionReportIndex => action => {
    switch (action) {
      case REPORT_ACTIONS.SELECT:
        this.onUpdateSelectedCollectionReportIndex(collectionReportIndex);
        break;
      case REPORT_ACTIONS.ADD:
        this.onMenuReportAction("blank", MENU_REPORT_ACTIONS.ADD);
        break;

      case REPORT_ACTIONS.DUPLICATE:
        this.onAddCollectionReport(
          collectionReportIndex,
          _.cloneDeep(this.getCollectionReportByIndex(collectionReportIndex))
        );
        break;

      case REPORT_ACTIONS.CLOSE:
        this.onDeleteCollectionReport(collectionReportIndex);
        break;

      case REPORT_ACTIONS.MOVE_UP:
        if (
          this.onSwapCollectionReport(
            collectionReportIndex,
            collectionReportIndex - 1
          )
        )
          this.onUpdateSelectedCollectionReportIndex(collectionReportIndex - 1);
        break;

      case REPORT_ACTIONS.MOVE_DOWN:
        if (
          this.onSwapCollectionReport(
            collectionReportIndex,
            collectionReportIndex + 1
          )
        )
          this.onUpdateSelectedCollectionReportIndex(collectionReportIndex + 1);

        break;

      case REPORT_ACTIONS.RUN_REPORT:
        this.onRunCollectionReport(collectionReportIndex);
        break;

      case REPORT_ACTIONS.OPEN_REPORT_PROPS:
        this.openCollectionReportProps(collectionReportIndex);
        break;

      case REPORT_ACTIONS.FULL_SCREEN:
        this.fullScreenCollectionReport(collectionReportIndex);
        break;
      case REPORT_ACTIONS.SMALL_SCREEN:
        this.smallScreenCollectionReport(collectionReportIndex);
        break;
      default:
        console.error("NOT IMPLEMENTED - collection report action:", action);
        break;
    }

    this.logClickAction("collectionReport", {
      action: action,
      collectionReportIndex: collectionReportIndex
    });
  };

  openCollectionReportProps = collectionReportIndex => {
    this.onUpdateSelectedCollectionReportIndex(collectionReportIndex);
    this.showReportPropsSider();
  };

  fullScreenCollectionReport = collectionReportIndex => {
    this.onUpdateSelectedCollectionReportIndex(collectionReportIndex);
    this.fullScreenModeOn();
  };

  smallScreenCollectionReport = collectionReportIndex => {
    this.onUpdateSelectedCollectionReportIndex(collectionReportIndex);
    this.fullScreenModeOff();
  };

  getCollectionReportByIndex = collectionReportIndex => {
    const { collection } = this.state;

    return collection && collection[collectionReportIndex];
  };

  getCollectionReportData = collectionReportIndex => {
    const item = this.getCollectionReportByIndex(collectionReportIndex);

    return this.getReportData(item);
  };

  showReportPropsSider = () => {
    this.changeReportPropsSiderVisibility(false);
  };

  toggleReportPropsSider = () => {
    const { settings: { isReportPropsCollapsed } } = this.state;
    this.changeReportPropsSiderVisibility(!isReportPropsCollapsed);
  };

  hideReportPropsSider = () => {
    this.changeReportPropsSiderVisibility(true);
  };

  fullScreenModeOn = () => {
    this.changeReportPropsFullScreenMode(true);
  };

  fullScreenModeOff = () => {
    this.changeReportPropsFullScreenMode(false);
  };

  changeReportPropsFullScreenMode = mode => {
    this.setState(prevState => ({
      isFullScreen: mode
    }));
  };

  changeReportPropsSiderVisibility = hide => {
    this.setState(
      prevState => ({
        settings: {
          ...prevState.settings,
          isReportPropsCollapsed: hide
        }
      }),
      () => this.props.onUpdateSettings(this.state.settings)
    );
  };

  getFetchKey = (startDt, endDt, groupByPeriod) => {
    if (!startDt || !endDt || !groupByPeriod) return "";
    return `${startDt}-${endDt}-${groupByPeriod}`;
  };

  getReportData = report => {
    const { options, settings, fetchData, requests } = this.state;
    // const { selectedTimeAggregation } = settings;

    let data = [];
    if (!options || !settings || !report) return data;
    let refRowCount = 0;

    // const report = CoreDataTransformations.getSelectedReport(
    //   reports,
    //   selectedReportValue
    // );

    const groupTypes = CoreDataTransformations.getGroupTypesFromReport(report);
    // console.log("getReportData", report, groupTypes);
    groupTypes.forEach(groupType => {
      const groupData = CoreDataTransformations.getRowData(
        groupType,
        CoreDataTransformations.getEnabledSelectedOptions(
          options[`${groupType}_variables`],
          report.groups[groupType].variables.selectedValues,
          null //TOFIX
        ),
        CoreDataTransformations.getEnabledSelectedOptions(
          options[`${groupType}_objects`], //note this is objects not elements, should fix it
          report.groups[groupType].elements.selectedValues,
          null //TOFIX
        ),
        fetchData &&
          fetchData[report.fetchKey] &&
          fetchData[report.fetchKey][groupType],
        refRowCount
      );
      refRowCount += groupData.length;
      data = data.concat(groupData);
    });

    const reportTitle = CoreDataTransformations.getReportTitle(report);
    const reportSubTitle = CoreDataTransformations.getReportSubTitle(
      report,
      options,
      settings
    );

    const reportStatus = CoreDataTransformations.isReportLoading(
      report,
      requests
    )
      ? REPORT_STATUS.LOADING
      : REPORT_STATUS.INIT;
    return {
      data: data,
      title: reportTitle,
      subtitle: reportSubTitle,
      status: reportStatus
    };
  };

  render() {
    // console.log("STATE", this.state);
    const { Sider, Content } = Layout;
    const {
      tableWidth,
      tableHeight,
      requests,
      lastRequestTime,
      options,
      //fetchData,
      collection,
      selectedCollectionReportIndex,
      reports,
      themes,
      isFullScreen,
      settings,
      settings: {
        view,
        selectedChartTooltipsType,
        selectedTimeAggregation,
        selectedTimePeriod,
        numberOfChartsToShow,
        showLegend,
        startDt
      }
    } = this.state;

    const latestRequestsIds = Object.keys(requests).filter(
      r => requests[r].requestTime === lastRequestTime
    );

    const isLoading =
      latestRequestsIds.filter(
        r => requests[r].status === REQUEST_STATUS.LOADING
      ).length > 0;
    const percentLoaded =
      latestRequestsIds.filter(
        r =>
          //  requests[r].status === REQUEST_STATUS.LOADING ||
          requests[r].status === REQUEST_STATUS.SUCCESS ||
          requests[r].status === REQUEST_STATUS.ERROR
      ).length /
      latestRequestsIds.length *
      100;
    return (
      <div className={"cp-performance"}>
        {this.state.page.loading ? (
          <Spinner className="page-loader" />
        ) : this.state.page.error ? (
          <ErrorIcon
            className="page-error"
            message="An error occurred while loading the page, try reloading (Ctrl + F5). If error persists, contact pdView."
          />
        ) : (
          <Layout>
            <Sider
              collapsible
              collapsedWidth={0}
              style={{
                backgroundColor: "#fff",
                height: "100vh"
              }}
              collapsed={settings.isCatalogueCollapsed}
              onCollapse={this.onCollapseCatalogue}
            >
              <div>
                <CoreReportMenu
                  reports={reports}
                  isCollapsed={settings.isCatalogueCollapsed}
                  selectedValue={settings.selectedReportValue}
                  onMenuReportAction={this.onMenuReportAction}
                />
              </div>
            </Sider>
            <Layout style={{ overflow: "hidden" }}>
              <Content>
                <div
                  style={{
                    padding: "4px",
                    paddingLeft: "40px",
                    boxShadow: "0px 0px 10px 0px #333",
                    textAlign: "center",
                    backgroundColor: "#dedede"
                  }}
                >
                  <div>
                    <ButtonViews
                      selectedView={view}
                      onClick={this.onChangeView}
                    />{" "}
                    <Radio.Group
                      defaultValue={numberOfChartsToShow}
                      onChange={e =>
                        this.setState(
                          prevState => ({
                            settings: {
                              ...prevState.settings,
                              numberOfChartsToShow: e.target.value
                            }
                          }),
                          () => this.props.onUpdateSettings(this.state.settings)
                        )
                      }
                    >
                      <Radio.Button value={1}>
                        <IconLight icon={"minus"} />
                      </Radio.Button>
                      <Radio.Button value={2}>
                        <IconLight icon={"equals"} />
                      </Radio.Button>
                      <Radio.Button value={3}>
                        <IconLight icon={"bars"} />
                      </Radio.Button>
                    </Radio.Group>{" "}
                    <span
                      style={{
                        visibility:
                          view === ButtonViewTypes.Chart ? "visible" : "hidden"
                      }}
                    >
                      <ButtonChartTooltips
                        onClick={this.onChangeChartTooltipsType}
                        selectedValue={selectedChartTooltipsType}
                      />{" "}
                      <Button
                        //type={showLegend ? "default" : "dashed"}
                        style={{
                          border: showLegend
                            ? "1px solid rgb(19, 168, 158)"
                            : "1px solid transparent",
                          color: showLegend ? "rgb(19, 168, 158)" : "inherit"
                        }}
                        onClick={() =>
                          this.setState(
                            prevState => ({
                              settings: {
                                ...prevState.settings,
                                showLegend: !prevState.settings.showLegend
                              }
                            }),
                            () =>
                              this.props.onUpdateSettings(this.state.settings)
                          )
                        }
                      >
                        <IconLight icon={"list-alt"} /> Legend
                      </Button>
                    </span>
                  </div>
                  {requests &&
                    latestRequestsIds && (
                      <div style={{ maxWidth: "80%", margin: "auto" }}>
                        <Progress
                          title={
                            latestRequestsIds.filter(
                              r => requests[r].status === REQUEST_STATUS.ERROR
                            ).length > 0
                              ? "Something went wrong while fetching data, please try again. If error persists, please contact pdView"
                              : ""
                          }
                          percent={
                            percentLoaded ? Math.round(percentLoaded) : 0
                          }
                          status={
                            latestRequestsIds.filter(
                              r => requests[r].status === REQUEST_STATUS.ERROR
                            ).length > 0
                              ? "exception"
                              : latestRequestsIds.filter(
                                  r =>
                                    requests[r].status ===
                                    REQUEST_STATUS.LOADING
                                ).length > 0
                                ? "active"
                                : "normal"
                          }
                          strokeWidth={4}
                          showInfo={true}
                          // successPercent={
                          //   Object.keys(requests).filter(
                          //     r => requests[r].status === REQUEST_STATUS.SUCCESS
                          //   ).length /
                          //   latestRequestsIds.length *
                          //   100
                          // }
                        />
                      </div>
                    )}
                  <PerformanceToolbar
                    isLoading={isLoading}
                    onUpdateTimePeriod={this.onTimePeriodUpdate}
                    startDateTime={startDt}
                    timeAggregationOptions={options.timeAggregation}
                    timeAggregation={selectedTimeAggregation}
                    timePeriod={selectedTimePeriod}
                    reportCount={collection && collection.length}
                  />
                </div>
                <Layout>
                  <Content>
                    <div
                      style={{
                        overflowY: "scroll",
                        height: tableHeight + 120
                      }}
                    >
                      {collection &&
                        collection.map((item, i) => (
                          <CoreReport
                            key={`${item.name}-${item.similarReportCounter}`}
                            height={(tableHeight - 150) / numberOfChartsToShow}
                            width={tableWidth}
                            view={view}
                            data={this.getCollectionReportData(i)}
                            isSelected={selectedCollectionReportIndex === i}
                            dataMode={null}
                            theme={
                              item && item.themeId ? themes[item.themeId] : {}
                            }
                            onReportAction={this.onCollectionReportAction(i)}
                            showLegend={showLegend}
                            isTooltipSplit={
                              selectedChartTooltipsType ===
                              ButtonChartTooltipsTypes.Multiple
                            }
                            isFullScreen={isFullScreen}
                          />
                        ))}
                    </div>
                  </Content>
                </Layout>
              </Content>

              <Sider
                className={"right-sider"}
                collapsedWidth={0}
                collapsible
                reverseArrow={true}
                width={320}
                collapsed={settings.isReportPropsCollapsed}
                onCollapse={this.toggleReportPropsSider}
                style={{
                  background: "#13a89e",
                  color: "white"
                }}
              >
                {collection &&
                  collection[selectedCollectionReportIndex] && (
                    <div>
                      <CoreReportProperties
                        report={collection[selectedCollectionReportIndex]}
                        options={options}
                        selectedValues={settings}
                        onUpdate={this.onUpdateCollectionReportProperties(
                          selectedCollectionReportIndex
                        )}
                      />
                    </div>
                  )}
              </Sider>
            </Layout>
          </Layout>
        )}
      </div>
    );
  }
}

Performance.propTypes = {
  onLogAction: PropTypes.func.isRequired,
  onUpdateSettings: PropTypes.func.isRequired,
  settings: PropTypes.object
};

export default Performance;



// WEBPACK FOOTER //
// ./src/views/performance/Performance.jsx